// layout.section.v1
//
// A vertical stack with a heading + optional toolbar + body. Replaces the
// hand-rolled section blocks scattered across view-renderer.tsx and the
// plugin-specific renderers.
//
// Spacing is fixed by tokens — plugins cannot override it. That's the
// whole point of the registry; it's what gives every plugin a consistent
// rhythm.

import { type JSX, Show, splitProps } from "solid-js"

export type SectionDensity = "comfortable" | "compact"

export interface SectionProps {
  title?: JSX.Element
  description?: JSX.Element
  toolbar?: JSX.Element
  density?: SectionDensity
  children?: JSX.Element
  class?: string
  classList?: Record<string, boolean | undefined>
}

const densityClasses = {
  comfortable: "gap-4 py-4",
  compact: "gap-2 py-2",
} as const satisfies Record<SectionDensity, string>

export function Section(props: SectionProps) {
  const [local, rest] = splitProps(props, ["title", "description", "toolbar", "density", "children", "class", "classList"])

  return (
    <section
      data-component="layout.section"
      data-density={local.density ?? "comfortable"}
      classList={{
        "flex flex-col min-w-0": true,
        [densityClasses[local.density ?? "comfortable"]]: true,
        [local.class ?? ""]: !!local.class,
        ...(local.classList ?? {}),
      }}
      {...rest}
    >
      <Show when={local.title || local.toolbar}>
        <header data-slot="section-header" class="flex items-start justify-between gap-3 min-w-0">
          <Show when={local.title || local.description}>
            <div class="flex flex-col gap-1 min-w-0">
              <Show when={local.title}>
                <div data-slot="section-title" class="text-15-medium text-text-strong truncate">
                  {local.title}
                </div>
              </Show>
              <Show when={local.description}>
                <div data-slot="section-description" class="text-13-regular text-text-base">
                  {local.description}
                </div>
              </Show>
            </div>
          </Show>
          <Show when={local.toolbar}>
            <div data-slot="section-toolbar" class="shrink-0 flex items-center gap-2">
              {local.toolbar}
            </div>
          </Show>
        </header>
      </Show>
      <Show when={local.children}>
        <div data-slot="section-body" class="min-w-0">
          {local.children}
        </div>
      </Show>
    </section>
  )
}
